/**
 * ClientApp/src/apps/components/mapViewers/tests/PieMap/utils/clusterPieSmartMapping.js
 *
 * Smart-mapping cluster pie implementation for the PieMap sandbox.
 *
 * Confirmed-working ArcGIS pattern:
 *
 * 1. Load FeatureLayer.
 * 2. Create a real UniqueValueRenderer.
 * 3. Use real SimpleMarkerSymbol instances for point features.
 * 4. Keep explicit uniqueValueInfos <= 10.
 * 5. Let defaultSymbol/defaultLabel handle all remaining values as "Other".
 * 6. Assign layer.renderer BEFORE createRendererForClustering().
 * 7. Pass fieldName to createRendererForClustering().
 */

import UniqueValueRenderer from "@arcgis/core/renderers/UniqueValueRenderer.js";
import SimpleMarkerSymbol from "@arcgis/core/symbols/SimpleMarkerSymbol.js";
import * as clusterLabelCreator from "@arcgis/core/smartMapping/labels/clusters.js";
import * as pieChartRendererCreator from "@arcgis/core/smartMapping/renderers/pieChart.js";

const MAX_CATEGORIES = 10;

/**
 * Applies a smart-mapping generated cluster pie renderer.
 *
 * @param {FeatureLayer} layer
 * @param {MapView|SceneView} view
 * @param {Object} options
 * @param {string} options.fieldName
 * @param {{value:string,label:string,color:string}[]} options.categories
 * @param {string} [options.otherLabel="Other"]
 * @param {string} [options.otherColor="#808080"]
 * @param {"pie"|"donut"} [options.shape="donut"]
 * @param {number} [options.holePercentage=0.66]
 */
export async function applySmartClusterPieRenderer(
  layer,
  view,
  options = {}
) {
  const {
    fieldName = "CURRENT_STATUS",
    categories = [],
    otherLabel = "Other",
    otherColor = "#808080",
    shape = "donut",
    holePercentage = 0.66,
  } = options;

  if (!layer || !view) {
    throw new Error(
      "[clusterPieSmartMapping] layer and view are required."
    );
  }

  if (!categories.length) {
    throw new Error(
      "[clusterPieSmartMapping] categories are required."
    );
  }

  if (categories.length > MAX_CATEGORIES) {
    throw new Error(
      `[clusterPieSmartMapping] ArcGIS cluster pie supports a maximum ` +
      `of ${MAX_CATEGORIES} explicit categories. Received ${categories.length}.`
    );
  }

  await layer.load();

  /*
   * These are POINT features, so use SimpleMarkerSymbol.
   *
   * Do not replace this with SimpleFillSymbol.
   */
  const createMarkerSymbol = (color) =>
    new SimpleMarkerSymbol({
      size: 6,
      color,
      outline: {
        color: "white",
        width: 0.5,
      },
    });

  /*
   * Build a REAL UniqueValueRenderer.
   *
   * Only the explicitly configured statuses become uniqueValueInfos.
   * Everything else is represented by defaultSymbol/defaultLabel ("Other").
   */
  const uniqueValueRenderer = new UniqueValueRenderer({
    field: fieldName,

    defaultLabel: otherLabel,
    defaultSymbol: createMarkerSymbol(otherColor),

    uniqueValueInfos: categories.map(({
      value,
      label,
      color
    }) => ({
      value,
      label: label ?? value,
      symbol: createMarkerSymbol(color),
    })),
  });

  /*
   * CRITICAL:
   *
   * createRendererForClustering() reads the layer renderer.
   * The UniqueValueRenderer therefore must be assigned FIRST.
   */
  layer.renderer = uniqueValueRenderer;

  /*
   * Generate the cluster label scheme after the layer renderer has
   * been configured.
   */
  const labelSchemes =
    await clusterLabelCreator.getLabelSchemes({
      layer,
      view,
    });

  const {
    labelingInfo,
    clusterMinSize
  } = labelSchemes.primaryScheme;

  const labelSymbol = labelingInfo?.[0]?.symbol;

  if (labelSymbol?.haloColor) {
    labelSymbol.color = labelSymbol.haloColor.clone();
    labelSymbol.haloColor = [255, 255, 255, 0.3];
  }

  if (labelSymbol?.font) {
    labelSymbol.font.size = 10;
  }

  /*
   * Generate the actual cluster pie renderer.
   *
   * Both pieces matter:
   *
   *   layer.renderer = uniqueValueRenderer
   *
   * AND
   *
   *   fieldName
   */
  const {
    renderer,
    fields
  } = await pieChartRendererCreator.createRendererForClustering({
    layer,
    view,
    fieldName,
    shape,
  });

  renderer.holePercentage = holePercentage;

  /*
   * Popup uses the aggregate fields generated by
   * createRendererForClustering().
   */
  const fieldInfos = fields.map((field) => ({
    fieldName: field.name,
    label: field.alias,
    format: {
      places: 0,
      digitSeparator: true,
    },
  }));

  const fieldNames =
    fieldInfos.map((fieldInfo) => fieldInfo.fieldName);

  const popupTemplate = {
    title: "Well Cluster",

    content: [
      {
        type: "text",
        text:
          "This cluster represents <b>{cluster_count}</b> wells.",
      },
      {
        type: "media",
        mediaInfos: [{
          title: `${fieldName} Distribution`,
          type: "pie-chart",
          value: {
            fields: fieldNames,
          },
        }],
      },
      {
        type: "fields",
      },
    ],

    fieldInfos,
  };

  layer.featureReduction = {
    type: "cluster",
    popupTemplate,
    labelingInfo,
    clusterMinSize,
    fields,
    renderer,
  };

  console.log(
    `[clusterPieSmartMapping] Smart pie applied:`,
    {
      layer: layer.title,
      fieldName,
      categories: categories.length,
      fields,
      renderer,
    }
  );

  return layer.featureReduction;
}